import React, { useState, useMemo, useEffect } from 'react';
import { Table, TableBody, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import { Invoice, InvoiceGroupDetails, InvoiceLineItem, SKU } from '../../../redux/billing/billing.types';
import { useUpdateInvoiceMutation, useUpdateInvoiceLineItemMutation, useCreateInvoiceMutation, useResetInvoiceLineItemMutation, useResetInvoiceMutation, useDestroyInvoiceLineItemMutation } from '../../../redux/billing/billing.endpoints';
import { useGetPlatformsQuery } from "../../../redux/admin/reporting/reporting.endpoints";
import { useGetTermsQuery, useGetSKUsQuery } from "../../../redux/billing/billing.endpoints";

import {
  InvoiceRow,
  getCompanyName,
  getInvoiceChannelDisplay,
  formatCurrency,
} from './invoice-table/';
import { Eye, EyeOff, Filter, Plus, RefreshCw, Upload } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import ValidateUploadContainer from './invoice-table/validate-upload-container';
import InvoiceCreationContainer from './invoice-table/invoice-creation';
import { Company } from 'src/pages/billing/types/billing.types';
import { useGetCampaignsQuery, useGetInternalUsersQuery } from 'src/pages/client-management/endpoints/accounts.endpoints';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxState } from 'src/redux/redux-state';
import InvoiceFilter, { FilterSelections } from './invoice-table/invoice-filter';
import { User } from 'src/services/user/user.types';
import { useNavigate } from 'react-router-dom';
import InvoiceDrawer from './invoice-table/invoice-drawer';
import notificationService from 'src/services/notification/notification.service';
import { isInvoiceLocked } from '../utils/billing-utils';

interface InvoiceTableProps {
  searchValue: string;
  selectedGroupId: string;
  invoiceGroupDetails?: InvoiceGroupDetails;
  isLoadingDetails: boolean;
  isFetchingDetails: boolean;
  companies?: Company[];
  isGeneratingInvoices?: boolean;
  onGenerationComplete?: () => void;
  onOpenPreviewModal?: (groupId: string) => void;
  onGenerateInvoices?: (groupId: string) => void;
  refetchInvoiceGroups?: () => void;
}

export const InvoiceTableRefactored = ({
  searchValue,
  selectedGroupId,
  invoiceGroupDetails,
  isLoadingDetails,
  isFetchingDetails,
  companies = [],
  refetchInvoiceGroups,
}: InvoiceTableProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [pendingInvoiceId, setPendingInvoiceId] = useState<string | null>(null);
  const [showFilterContainer, setShowFilterContainer] = useState(false);
  const [expandedInvoices, setExpandedInvoices] = useState<Set<string>>(new Set());
  const [viewHidden, setViewHidden] = useState(false);
  const [showValidateUploadContainer, setShowValidateUploadContainer] = useState(false);
  const [showInvoiceCreationContainer, setShowInvoiceCreationContainer] = useState(false);
  const [totalAmountToBill, setTotalAmountToBill] = useState(0);
  const [showInvoiceDrawer, setShowInvoiceDrawer] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | undefined>(undefined);
  const [selectedSkus, setSelectedSkus] = useState<Array<{id: string, name: string}>>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<Array<{id: string, name: string}>>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<Array<{id: string, name: string}>>([]);
  const [selectedOwners, setSelectedOwners] = useState<Array<{id: string, name: string}>>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<Array<{id: string, name: string}>>([]);

  // Local & optimistic states for patches & generation
  const [textFieldValues, setTextFieldValues] = useState<Record<string, string>>({});
  const [optimisticInvoiceGroupDetails, setOptimisticInvoiceGroupDetails] = useState<InvoiceGroupDetails | undefined>(invoiceGroupDetails);
  const [isLoading, setIsLoading] = useState(false);
  const [filterUsersInvoices, setFilterUsersInvoices] = useState(false);
  const [createInvoice, { isLoading: isCreatingInvoice }] = useCreateInvoiceMutation();
  const [updateInvoice] = useUpdateInvoiceMutation();
  const [updateInvoiceLineItem, { isLoading: isUpdatingInvoiceLineItem }] = useUpdateInvoiceLineItemMutation();
  const [resetInvoice, { isLoading: isResettingInvoice }] = useResetInvoiceMutation();
  const [resetInvoiceLineItem, { isLoading: isResettingInvoiceLineItem }] = useResetInvoiceLineItemMutation();
  const [destroyInvoiceLineItem, { isLoading: isDestroyingInvoiceLineItem }] = useDestroyInvoiceLineItemMutation();

  const userState = useSelector((state: ReduxState) => state.userState);
  const {data: internalUsers = []} = useGetInternalUsersQuery();
  const { data: platforms = [] } = useGetPlatformsQuery({});
  const { data: terms = [] } = useGetTermsQuery();
  const { data: skus = [] } = useGetSKUsQuery();
  const { data: campaigns = [] } = useGetCampaignsQuery({});

  useEffect(() => {
    setIsLoading(isFetchingDetails || isLoadingDetails || isUpdatingInvoiceLineItem || isResettingInvoiceLineItem || isCreatingInvoice || isResettingInvoice);
  }, [isFetchingDetails, isLoadingDetails, isUpdatingInvoiceLineItem, isResettingInvoiceLineItem, isCreatingInvoice, isResettingInvoice]);

  useEffect(() => {
    if (invoiceGroupDetails) {
      setOptimisticInvoiceGroupDetails(invoiceGroupDetails);
    }
  }, [invoiceGroupDetails]);
  // Add this useEffect after the existing useEffects (around line 107)

  useEffect(() => {
    // When invoiceGroupDetails is refetched, update selectedInvoice if it exists
    if (selectedInvoice && invoiceGroupDetails) {
      const updatedInvoice = invoiceGroupDetails.invoices.find(
        inv => inv.id === selectedInvoice.id
      );
      if (updatedInvoice) {
        setSelectedInvoice(updatedInvoice);
      }
    }
  }, [invoiceGroupDetails, selectedInvoice?.id]);


  const handleCreateNewInvoice = async(companyId: string) => {
    try {
      const invoice = await createInvoice({
        company: companyId,
        invoice_group: selectedGroupId,
        attention_needed: true,
      }).unwrap();

      setPendingInvoiceId(invoice.id);

      if (refetchInvoiceGroups) {
        await refetchInvoiceGroups();
      }

      setShowInvoiceCreationContainer(false);
    }
    catch (error) {
      console.error('Failed to create invoice:', error);
    }
  };

  useEffect(() => {
    if (pendingInvoiceId && invoiceGroupDetails) {
      const foundInvoice = invoiceGroupDetails.invoices?.find(
        inv => inv.id === pendingInvoiceId
      );

      if (foundInvoice) {
        // Ensure invoice_line_items is always an array
        const normalizedInvoice = {
          ...foundInvoice,
          invoice_line_items: foundInvoice.invoice_line_items || [],
        };

        setSelectedInvoice(normalizedInvoice);
        setShowInvoiceDrawer(true);
        setPendingInvoiceId(null); // Clear the pending ID
      }
    }
  }, [pendingInvoiceId, invoiceGroupDetails]);

  // Lookup objects (keeping existing logic)
  const companyLookup = useMemo(() => {
    if (!companies) return {};
    return companies.reduce((acc, company) => {
      acc[company.id] = company;
      return acc;
    }, {} as Record<string, Company>);
  }, [companies]);


  // Sorted and filtered invoices - unassigned first, hide if no line items
  const sortedInvoices = useMemo(() => {
    const invoices = optimisticInvoiceGroupDetails?.invoices || [];
    return invoices
      .slice()
      // Filter out unassigned invoices with no line items
      .filter(invoice => {
        const isUnassigned = !invoice.company || !companyLookup[invoice.company];
        if (isUnassigned && invoice.invoice_line_items.length === 0) {
          return false; // Hide unassigned invoices with no line items
        }
        return true;
      })
      .sort((a, b) => {
        const aIsUnassigned = !a.company || !companyLookup[a.company];
        const bIsUnassigned = !b.company || !companyLookup[b.company];

        // Unassigned invoices always come first
        if (aIsUnassigned && !bIsUnassigned) return -1;
        if (!aIsUnassigned && bIsUnassigned) return 1;

        // If both are unassigned or both are assigned, sort by company name
        const aCompanyName = getCompanyName(a.company, companyLookup);
        const bCompanyName = getCompanyName(b.company, companyLookup);
        return aCompanyName.localeCompare(bCompanyName);
      })
      .map(invoice => ({
        ...invoice,
        invoice_line_items: invoice.invoice_line_items.slice().sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }, [optimisticInvoiceGroupDetails, companyLookup]);

  const filteredInvoices = useMemo(() => {
    if (!searchValue.trim() && selectedSkus.length === 0 && selectedStatuses.length === 0 && selectedPlatforms.length === 0 && selectedOwners.length === 0 && selectedCompanies.length === 0) {
      return sortedInvoices;
    }

    const searchLower = searchValue.toLowerCase();
    return sortedInvoices.filter(invoice => {
      const companyMatch = selectedCompanies.length === 0 || selectedCompanies.some(company => company.id === invoice.company);
      const statusMatch = selectedStatuses.length === 0 || selectedStatuses.some(status => status.id === invoice.invoice_status);

      const skuMatch = selectedSkus.length === 0 || invoice.invoice_line_items.some(lineItem =>
        selectedSkus.some(sku => sku.id === lineItem.sku)
      );

      const platformMatch = selectedPlatforms.length === 0 || invoice.invoice_line_items.some(lineItem =>
        selectedPlatforms.some(platform => platform.id === lineItem.platform)
      );

      const ownerMatch = selectedOwners.length === 0 || invoice.invoice_line_items.some(lineItem => {
        if (!lineItem.campaign) return false;
        const campaign = campaigns?.find(c => c.id === lineItem.campaign);
        if (!campaign?.owner) return false;
        return selectedOwners.some(owner => owner.id === campaign.owner);
      });

      const invoiceMatches =
        getCompanyName(invoice.company || '', companyLookup, [invoice]).toLowerCase().includes(searchLower) ||
        getInvoiceChannelDisplay(invoice).toLowerCase().includes(searchLower) ||
        (companyLookup[invoice.company]?.emails || '').toLowerCase().includes(searchLower);

      const lineItemMatches = invoice.invoice_line_items.some(lineItem => {
        return (
          (lineItem.name || '').toLowerCase().includes(searchLower) ||
          (lineItem.description || '').toLowerCase().includes(searchLower) ||
          (lineItem.notes && lineItem.notes.toLowerCase().includes(searchLower))
        );
      });

      return companyMatch && statusMatch && skuMatch && platformMatch && ownerMatch && (invoiceMatches || lineItemMatches);
    });
  }, [sortedInvoices, searchValue, companyLookup, selectedSkus, selectedStatuses, selectedPlatforms, selectedOwners, selectedCompanies]);

  const clearLineItemTextFields = (lineItemId: string) => {
    setTextFieldValues(prev => {
      const newValues = { ...prev };
      Object.keys(newValues).forEach(key => {
        if (key.endsWith(`_${lineItemId}`)) {
          delete newValues[key];
        }
      });
      return newValues;
    });
  };

  // Toggle functions
  const toggleInvoiceExpansion = (invoiceId: string) => {
    const newExpanded = new Set(expandedInvoices);
    if (newExpanded.has(invoiceId)) {
      newExpanded.delete(invoiceId);
    } else {
      newExpanded.add(invoiceId);
    }
    setExpandedInvoices(newExpanded);
  };

  // Update functions (keeping existing logic but simplified)
  const updateInvoiceField = async(invoiceId: string, field: string, value: any) => {
    try {
      const invoice = filteredInvoices.find(inv => inv.id === invoiceId);
      if (!invoice) {
        return;
      }

      if (field === 'reset') {
        await resetInvoice({ id: invoiceId }).unwrap();
        if (refetchInvoiceGroups) {
          await refetchInvoiceGroups();
        }
        return;
      }

      await updateInvoice({
        id: invoiceId,
        data: {
          [field]: value,
          company: field === 'company' ? value : invoice.company,
          invoice_group: selectedGroupId,
        },
      }).unwrap();


      if (refetchInvoiceGroups) {
        refetchInvoiceGroups();
      }
      notificationService.handleSuccess('Invoice updated successfully', dispatch, 'Invoice updated successfully');
    } catch (error) {
      console.error('Failed to update invoice:', error);
      setOptimisticInvoiceGroupDetails(invoiceGroupDetails);
      notificationService.handleError(error, dispatch, 'Failed to update invoice');
    }
  };

  const updateLineItemField = async(lineItemId: string, field: string, value: any) => {
    try {
      if (field === 'status') {
        await destroyInvoiceLineItem({ id: lineItemId }).unwrap();
        if (refetchInvoiceGroups) {
          await refetchInvoiceGroups();
        }
        return;
      }

      if (field === 'reset') {
        await resetInvoiceLineItem({ id: lineItemId }).unwrap();
        if (refetchInvoiceGroups) {
          await refetchInvoiceGroups();
        }
        return;
      }

      await updateInvoiceLineItem({
        id: lineItemId,
        data: {
          [field]: value,
          invoice: field === 'invoice' ? value : selectedInvoice?.id,
          invoice_group: selectedGroupId,
        },
      }).unwrap();

      if (refetchInvoiceGroups) {
        await refetchInvoiceGroups();
      }
      notificationService.handleSuccess('Line item updated successfully', dispatch, 'Line item updated successfully');
    } catch (error) {
      console.error('Failed to update line item:', error);
      setOptimisticInvoiceGroupDetails(invoiceGroupDetails);
      clearLineItemTextFields(lineItemId);
      notificationService.handleError(error, dispatch, 'Failed to update line item');
    }
  };

  // Helper function
  const lineItemsNeedAttention = (invoice: Invoice) => {
    return invoice.invoice_line_items.some(item => item.attention_needed);
  };

  const filteredAllInvoices = useMemo(() => {
    if (!filterUsersInvoices) {
      return filteredInvoices;
    }

    const activeUser = internalUsers.find(user => user.ext_user_id === userState.extUserId);
    if (!activeUser) {
      return filteredInvoices;
    }

    return filteredInvoices.filter(invoice =>
      invoice.invoice_line_items.some(lineItem =>
        campaigns?.some(campaign =>
          campaign.id === lineItem.campaign && campaign.owner === activeUser?.id
        )
      )
    );
  }, [filterUsersInvoices, filteredInvoices, internalUsers, userState.extUserId, campaigns]);

  useEffect(() => {
    if (filteredAllInvoices) {
      const dnbSkuId = skus.find((sku: SKU) => sku.sku === 'DNB')?.id;
      setTotalAmountToBill(filteredAllInvoices.reduce((acc, invoice) => acc + invoice.invoice_line_items.reduce((acc, lineItem) => acc + (lineItem.sku === dnbSkuId ? 0 : lineItem.amount_to_bill || 0), 0), 0));
    }
  }, [filteredAllInvoices]);

  // TODO: Add this to invoice row as a list of owners
  const getOwner = (lineItem: InvoiceLineItem) => {
    const ownerEmail = internalUsers?.find(user => user.id === campaigns?.find(campaign => campaign.id === lineItem.campaign)?.owner)?.email;
    return ownerEmail || 'Unassigned';
  };

  const formatPlatform = (platform: string) => {
    return platform.replace('_', ' ').toUpperCase();
  };

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').toUpperCase();
  };

  const unassignedInvoicesColor = (numUnassignedInvoices: number) => {
    if (numUnassignedInvoices === 0) {
      return 'text-gray-500';
    } else {
      return 'bg-red-501 text-white hover:bg-red-700';
    }
  };

  // Main table render
  return (
    <div className="flex-grow min-h-0 flex flex-col">
      {/* Used for editing and creating an invoice */}
      <InvoiceDrawer
        open={showInvoiceDrawer}
        onOpenChange={setShowInvoiceDrawer}
        invoice={selectedInvoice}
        companies={companies}
        skus={skus}
        terms={terms}
        invoices={sortedInvoices}
        updateInvoice={updateInvoiceField}
        updateLineItem={updateLineItemField}
        refetchInvoiceGroup={() => refetchInvoiceGroups?.()}
      />

      <div className={`w-full bg-white border border-gray-200 flex-grow min-h-0 flex flex-col rounded-xl relative ${isLoading ? 'opacity-50' : ''}`}>
        {isLoading && (
          <div className="absolute top-2 left-2 flex justify-left items-center z-50 p-3 bg-blue-500/80 text-white rounded-full w-fit">
            <RefreshCw className="h-5 w-5 text-white animate-bounce animate-spin" />
          </div>
        )}
        <div className="flex-grow min-h-0 overflow-auto rounded-lg">
          {/* Menubar */}
          {/* TODO: Move this to a separate component */}
          <div className='flex flex-row border-b border-gray-201'>
            <div className="flex flex-row justify-start">
              <Button
                size='med'
                variant='ghost'
                title='Navigate to Manage Accounts'
                onClick={(() => navigate('/manage-clients/manage-accounts'))}
                className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 items-center ${filterUsersInvoices ? 'bg-gray-201' : ''} ${unassignedInvoicesColor(invoiceGroupDetails?.orphan_invoice_line_items.length || 0)}`}
              >
                <div className={`font-bold text-lg`}>
                  {invoiceGroupDetails?.orphan_invoice_line_items.length}
                </div>
                Unassigned
              </Button>
              <Button
                size='med'
                variant='ghost'
                onClick={(() => {})}
                className={`flex flex-col bg-green-501 text-white rounded-none p-8 px-4 pb-8 text-sm gap-1 items-center ${filterUsersInvoices ? 'bg-gray-201' : ''}`}
              >
                <div className='text-white font-bold text-lg'>
                  {formatCurrency(totalAmountToBill)}
                </div>
                Total Billed
              </Button>
              <Button
                size='med'
                variant='ghost'
                onClick={(() => {})}
                className={`flex flex-col bg-yellow-500 text-white rounded-none p-8 px-4 pb-8 text-sm gap-1 items-center ${filterUsersInvoices ? 'bg-gray-201' : ''}`}
              >
                <div className='text-white font-bold text-lg'>
                  {filteredInvoices.length}
                </div>
                Invoices
              </Button>
              <Button
                size='med'
                variant='ghost'
                onClick={(() => {})}
                className={`flex flex-col bg-orange-500 text-white rounded-none p-8 px-4 pb-8 text-sm gap-1 items-center ${filterUsersInvoices ? 'bg-gray-201' : ''}`}
              >
                <div className='text-white font-bold text-lg'>
                  {filteredInvoices.reduce((acc, invoice) => acc + invoice.invoice_line_items.length, 0)}
                </div>
                Line Items
              </Button>
            </div>
            <div className="flex flex-row gap-2 ml-auto">
              {/* add a filter expansion here */}
              <Button
                size='med'
                variant='ghost'
                onClick={(() => {
                  setShowInvoiceCreationContainer(false);
                  setShowValidateUploadContainer(false);
                  setShowFilterContainer(!showFilterContainer);
                })}
                className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 ${showFilterContainer ? 'bg-gray-201' : ''}`}
              >
                <Filter className="h-4 w-4" />
                Filter
              </Button>
              <Button
                size='med'
                variant='ghost'
                onClick={(() => {
                  setShowFilterContainer(false);
                  setShowValidateUploadContainer(!showValidateUploadContainer);
                  setShowInvoiceCreationContainer(false);
                })}
                className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 ${showValidateUploadContainer ? 'bg-gray-201' : ''}`}
              >
                <Upload className="h-4 w-4" />
                Validate
              </Button>
              <Button
                size='med'
                variant='ghost'
                onClick={(() => refetchInvoiceGroups?.())}
                className='flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1'
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
              <Button
                size='med'
                variant='ghost'
                onClick={(() => {
                  setViewHidden(!viewHidden);
                })}
                className='flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1'
              >
                {viewHidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {viewHidden ? 'Hide' : 'Show'}
              </Button>
              <Button
                size='med'
                variant='ghost'
                onClick={(() => {
                  setShowInvoiceCreationContainer(!showInvoiceCreationContainer);
                  setShowValidateUploadContainer(false);
                  setShowFilterContainer(false);
                })}
                className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 ${showInvoiceCreationContainer ? 'bg-gray-201' : ''}`}
              >
                <Plus className="h-4 w-4" />
                Invoice
              </Button>
            </div>
          </div>
          {showFilterContainer && (
            <InvoiceFilter
              skus={skus.map((sku: SKU) => ({id: sku.id, name: sku.name}))}
              statuses={['draft', 'approved', 'synced', 'paid', 'overdue', 'pending_sync', 'sync_error', 'needs_sync'].map((status: string) => ({id: status, name: formatStatus(status)}))}
              platforms={platforms.map((platform: string) => ({id: platform, name: formatPlatform(platform) || ''}))}
              owners={internalUsers.map((user: User) => ({id: user.id || '', name: user.email || ''}))}
              companies={companies.map((company: Company) => ({id: company.id, name: company.company_name || ''}))}
              onSelectionChange={(selections: FilterSelections) => {
                setSelectedSkus(selections.skus);
                setSelectedStatuses(selections.statuses);
                setSelectedPlatforms(selections.platforms);
                setSelectedOwners(selections.owners);
                setSelectedCompanies(selections.companies);
              }}
            />
          )}
          {showValidateUploadContainer && (
            <ValidateUploadContainer
              invoiceGroupId={selectedGroupId}
              onUpload={() => {
                setShowValidateUploadContainer(false);
                setTimeout(() => refetchInvoiceGroups?.(), 1000);
              }}
            />
          )}
          {showInvoiceCreationContainer && (
            <InvoiceCreationContainer
              isCreating={isCreatingInvoice}
              companies={companies}
              onCreate={(companyId: string) => {
                handleCreateNewInvoice(companyId);
              }}
            />
          )}

          {/* Table */}
          <Table>
            <TableHeader className="sticky top-0 bg-white z-10 rounded-t-lg">
              <TableRow className="border-b border-gray-201 rounded-t-lg">
                <TableHead className="w-[3%] min-w-[30px] py-1.5 rounded-tl-lg" />
                <TableHead className="text-gray-600 py-1.5 w-[35%] min-w-[250px] max-w-[250px]">
                  Invoice
                </TableHead>
                <TableHead className="text-gray-600 py-1.5 w-[15%] min-w-[70px] max-w-[70px]">
                  Channel
                </TableHead>
                <TableHead className="text-gray-600 py-1.5 text-right w-[15%] min-w-[100px] max-w-[100px]">
                  Quantity
                </TableHead>
                <TableHead className="text-gray-600 py-1.5 text-right w-[15%] min-w-[100px] max-w-[100px]">
                  Amount to Bill
                </TableHead>
                <TableHead className="text-gray-600 py-1.5 text-center w-[15%] min-w-[100px] max-w-[100px]">
                  Status
                </TableHead>
                <TableHead className="text-gray-600 py-1.5 text-center w-[3%] min-w-[30px] max-w-[30px]" />
              </TableRow>
            </TableHeader>
            <TableBody className='rounded-lg'>
              {filteredAllInvoices.filter(invoice => !viewHidden ? !invoice.hide : true).map((invoice, invoiceIndex) => {
                const isExpanded = expandedInvoices.has(invoice.id);
                const isUnassigned = !invoice.company || !companyLookup[invoice.company];
                const isFirstRow = invoiceIndex === 0;
                const isLastRow = invoiceIndex === filteredAllInvoices.length - 1;

                return (
                  <React.Fragment key={invoice.id}>
                    {/* Invoice Row */}
                    <InvoiceRow
                      isLocked={isInvoiceLocked(invoice, userState.permissions?.groups || [])}
                      invoice={invoice}
                      invoiceIndex={invoiceIndex}
                      isExpanded={isExpanded}
                      isFirstRow={isFirstRow}
                      isLastRow={isLastRow}
                      isUnassigned={isUnassigned}
                      viewHidden={viewHidden}
                      companyLookup={companyLookup}
                      selectedGroupId={selectedGroupId}
                      toggleInvoiceExpansion={(invoiceId: string) => {
                        setSelectedInvoice(invoice);
                        setShowInvoiceDrawer(true);
                        toggleInvoiceExpansion(invoiceId);
                      }}
                      updateInvoiceField={updateInvoiceField}
                      lineItemsNeedAttention={lineItemsNeedAttention}
                    />
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default InvoiceTableRefactored;
